import * as notebookSync from './notebookSync';
import { buildWeeklyReviewNotebookContent } from './weeklyReviews';

const FOLDER_ID = 'weekly-reviews-folder';

function safeId(value) {
  return String(value || 'account').replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'account';
}

export function getWeeklyReviewNotebookIds(accountId, weekKey) {
  const accountPart = safeId(accountId);
  return {
    folderId: FOLDER_ID,
    sectionId: `weekly-reviews-${accountPart}`,
    pageId: `weekly-review-${accountPart}-${safeId(weekKey)}`,
  };
}

export function canPersistWeeklyReviewOnThisBuild(account) {
  if (process.env.NODE_ENV === 'production') return true;
  if (typeof window === 'undefined') return false;
  const explicitlyEnabled = new URLSearchParams(window.location.search).get('weeklyReviewTest') === '1';
  const testAccount = /\b(test|qa|demo)\b/i.test(String(account?.name || ''));
  return explicitlyEnabled && testAccount;
}

function buildMetaOperation(account, review, ids) {
  return {
    type: 'upsertWeeklyReviewMeta',
    pageId: ids.pageId,
    folder: {
      id: ids.folderId,
      name: 'Weekly Reviews',
      color: '#3b82f6',
      collapsed: false,
      isFolder: true,
      parentId: null,
    },
    section: {
      id: ids.sectionId,
      name: account?.name || review.accountName || 'Account',
      color: '#3b82f6',
      collapsed: false,
      isFolder: false,
      parentId: ids.folderId,
    },
  };
}

async function writeReview(uid, account, review) {
  const ids = getWeeklyReviewNotebookIds(account?.id || review.accountId, review.weekKey);
  notebookSync.setCurrentUser(uid);
  await notebookSync.enqueueLatest({
    type: 'updatePage',
    pageId: ids.pageId,
    data: {
      sectionId: ids.sectionId,
      name: review.weekLabel,
      content: buildWeeklyReviewNotebookContent(review),
      stickyNotes: [],
      color: null,
      pageType: 'weeklyReview',
      weeklyReview: review,
    },
  });
  await notebookSync.enqueue(buildMetaOperation(account, review, ids));
  const result = await notebookSync.flushUntilSaved();
  if (!result?.success) throw new Error('Weekly review could not be saved to Notebook.');
  return { ids, review };
}

export async function ensureWeeklyReviewNotebookPage({ uid, account, review }) {
  if (!uid || !account?.id || !review) return { review, persisted: false };
  if (!canPersistWeeklyReviewOnThisBuild(account)) return { review, persisted: false, localSafetyGate: true };
  const ids = getWeeklyReviewNotebookIds(account.id, review.weekKey);
  notebookSync.setCurrentUser(uid);
  const existingPage = await notebookSync.fetchPage(uid, ids.pageId);
  const existingReview = existingPage?.weeklyReview;
  if (existingReview) {
    if (existingReview.sourceHash !== review.sourceHash) {
      return {
        review: existingReview,
        pendingReview: { ...review, reflections: existingReview.reflections || review.reflections },
        persisted: true,
        dataChanged: true,
        ids,
      };
    }
    return { review: existingReview, persisted: true, dataChanged: false, ids };
  }
  const saved = await writeReview(uid, account, review);
  return { ...saved, persisted: true, dataChanged: false };
}

export async function refreshWeeklyReviewNotebookPage({ uid, account, review, reflections }) {
  const nextReview = { ...review, reflections: reflections || review.reflections || {} };
  return writeReview(uid, account, nextReview);
}

export async function saveWeeklyReviewReflections({ uid, account, review, reflections }) {
  if (!canPersistWeeklyReviewOnThisBuild(account)) return { success: false, localSafetyGate: true };
  const nextReview = { ...review, reflections: { ...review.reflections, ...reflections } };
  const ids = getWeeklyReviewNotebookIds(account.id, review.weekKey);
  notebookSync.setCurrentUser(uid);
  await notebookSync.enqueueLatest({
    type: 'updatePage',
    pageId: ids.pageId,
    data: {
      content: buildWeeklyReviewNotebookContent(nextReview),
      weeklyReview: nextReview,
    },
  });
  const result = await notebookSync.flushUntilSaved();
  return { success: Boolean(result?.success), review: nextReview };
}
